import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, CalendarPicker, Popup, Checkbox, Tag } from 'antd-mobile';
import { DownOutline } from 'antd-mobile-icons';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import PullToRefreshWrapper from '../../../components/PullToRefreshWrapper';
import { formatLocalizedDate } from '../../../utils/dateFormatUtils';
import { DspPath } from '../../../enums/DeliveryNavigatePathEnum';
import { GET_AVAILABLE_DRIVERS, DriverType as GraphQLDriverType } from '../graphql/queries/getAvailableDrivers';
import { GET_DELIVERY_TASKS } from '../graphql/queries/getDeliveryTasks';
import { DeliveryTask, Driver, DeliveryTaskFilter, TaskStatus } from '../graphql/types/DeliveryTaskType';
import { ReactComponent as PackageSvg } from '../icons/svgs/package.svg';
import { ReactComponent as DriverSvg } from '../icons/svgs/driver.svg';
import DspLayout from "../layouts/DspLayout";


const DspDeliveryTaskPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // 日期选择状态
    const [showCalendar, setShowCalendar] = useState(false);
    const [singleDate, setSingleDate] = useState<Date>(new Date());

    // 司机选择状态
    const [showDriverSelector, setShowDriverSelector] = useState(false);
    const [selectedDriverIds, setSelectedDriverIds] = useState<number[]>([]);
    const [tempSelectedDriverIds, setTempSelectedDriverIds] = useState<number[]>([]);

    // GraphQL查询
    const { data: driversData, loading: driversLoading, error: driversError, refetch: refetchDrivers } = useQuery(GET_AVAILABLE_DRIVERS);

    // 从sessionStorage恢复状态，避免在URL中暴露敏感信息
    useEffect(() => {
        // 检查是否从派送任务相关页面跳转过来
        const fromDeliveryTask = sessionStorage.getItem('fromDeliveryTask');

        if (fromDeliveryTask === 'true') {
            // 从派送任务相关页面跳转，恢复之前的状态
            const savedDate = sessionStorage.getItem('selectedTaskDate');
            if (savedDate) {
                const date = dayjs(savedDate).toDate();
                if (!isNaN(date.getTime())) {
                    setSingleDate(date);
                }
            }

            const savedDriverIds = sessionStorage.getItem('selectedDriverIds');
            if (savedDriverIds) {
                try {
                    const driverIds = JSON.parse(savedDriverIds);
                    if (Array.isArray(driverIds)) {
                        // 确保转换为数字数组
                        const numericDriverIds = driverIds.map(id => Number(id)).filter(id => !isNaN(id));
                        setSelectedDriverIds(numericDriverIds);
                    }
                } catch (error) {
                    console.warn('Failed to parse saved driver IDs:', error);
                }
            }

            // 标记需要恢复滚动位置，稍后在数据加载完成后恢复
            // 不清除 fromDeliveryTask，等滚动位置恢复后再清除
        } else {
            // 从首页tab或运营数据tab跳转，重置为默认状态
            setSingleDate(new Date());
            setSelectedDriverIds([]);
        }
    }, []);

    // 保存用户选择的状态到 sessionStorage，供其他页面使用
    useEffect(() => {
        // 保存选中的日期
        const dateStr = dayjs(singleDate).format('YYYY-MM-DD'); // YYYY-MM-DD 格式
        sessionStorage.setItem('selectedTaskDate', dateStr);

        // 保存选中的司机ID
        if (selectedDriverIds.length > 0) {
            sessionStorage.setItem('selectedDriverIds', JSON.stringify(selectedDriverIds));
        } else {
            // 如果没有选中司机，清除存储的司机ID
            sessionStorage.removeItem('selectedDriverIds');
        }
    }, [singleDate, selectedDriverIds]);

    // 构建过滤条件
    const buildFilters = useMemo((): DeliveryTaskFilter => {
        const newFilters: DeliveryTaskFilter = {};

        // 处理日期过滤
        if (singleDate) {
            const selectedDate = dayjs(singleDate).format('YYYY-MM-DD'); // YYYY-MM-DD 格式
            newFilters.taskDateStart = selectedDate;
            newFilters.taskDateEnd = selectedDate;
        }

        // 处理司机过滤 - 空数组表示全部司机，不传该参数
        if (selectedDriverIds.length > 0) {
            newFilters.driverIds = selectedDriverIds;
        }

        return newFilters;
    }, [singleDate, selectedDriverIds]);

    // 获取派件任务列表
    const { data: tasksData, loading: tasksLoading, error: tasksError, refetch: refetchTasks } = useQuery(GET_DELIVERY_TASKS, {
        variables: {
            filter: buildFilters,
            pageRequest: {
                page: 1,
                size: 10000,
                sortBy: 'id',
                sortDirection: 'ASC'
            }
        },
        fetchPolicy: 'cache-and-network'
    });


    // 处理司机数据，添加"全部司机"选项
    const drivers: Driver[] = useMemo(() => {
        if (!driversData?.getAvailableDrivers) {
            return [{ id: 'all', name: t('pages.dsp.deliveryTask.allDrivers') }];
        }

        const allDrivers: Driver[] = [
            { id: 'all', name: t('pages.dsp.deliveryTask.allDrivers') },
            ...driversData.getAvailableDrivers.map((driver: GraphQLDriverType) => ({
                id: driver.id,
                name: driver.name
            }))
        ];

        return allDrivers;
    }, [driversData, t]);

    // 获取任务状态标签配置
    const getStatusTag = (status: string) => {
        const configs: Record<string, { text: string; backgroundColor: string; textColor: string }> = {
            [TaskStatus.CREATED]: {
                text: t('pages.dsp.deliveryTask.waitForPickup'),
                backgroundColor: '#F0F0F0',
                textColor: '#8C8C8C'
            },
            [TaskStatus.DRIVER_PICKINGUP]: {
                text: t('pages.dsp.deliveryTask.pickingUp'),
                backgroundColor: '#d0ecfa',
                textColor: '#1890FF'
            },
            [TaskStatus.DELIVERING]: {
                text: t('pages.dsp.deliveryTask.delivering'),
                backgroundColor: '#d0ecfa',
                textColor: '#1890FF'
            },
            [TaskStatus.COMPLETED]: {
                text: t('pages.dsp.deliveryTask.completed'),
                backgroundColor: '#effde0',
                textColor: '#54ba21'
            }
        };
        return configs[status] || {
            text: status,
            backgroundColor: '#F0F0F0',
            textColor: '#8C8C8C'
        };
    };

    // 任务数据
    // 原来：const tasks: DeliveryTask[] = tasksData?.getDeliveryTasks?.content || [];
    // 新增：铺平成一级数组，children也纳入tasks
    const rawTasks: DeliveryTask[] = tasksData?.getDeliveryTasks?.content || [];
    const tasks: DeliveryTask[] = useMemo(() => {
        const flat: DeliveryTask[] = [];
        for (const task of rawTasks) {
            if (task.isGroupRow) {
                if (Array.isArray(task.children) && task.children.length > 0) {
                    flat.push(...task.children);
                }
            } else {
                flat.push(task);
                if (Array.isArray(task.children) && task.children.length > 0) {
                    flat.push(...task.children);
                }
            }
        }
        return flat;
    }, [rawTasks]);

    // 统计数据
    const stats = useMemo(() => {
        const total = tasks.length;
        const receiving = tasks.filter(t => t.status === TaskStatus.DRIVER_PICKINGUP).length;
        const delivering = tasks.filter(t => t.status === TaskStatus.DELIVERING).length;
        const completed = tasks.filter(t => t.status === TaskStatus.COMPLETED).length;

        const totalAllocated = tasks.reduce((sum, task) => sum + (task.assignedParcels || 0), 0);
        const totalReceived = tasks.reduce((sum, task) => sum + (task.actualPickedUpParcels || 0), 0);

        return {
            total,
            receiving,
            delivering,
            completed,
            totalAllocated,
            totalReceived
        };
    }, [tasks]);

    // Label refs 用于统一高度
    const labelRefs = useRef<(HTMLDivElement | null)[]>([]);

    // 查找真正的滚动容器（DspLayout 的内容区域）
    const getScrollContainer = (): HTMLElement | null => {
        return document.querySelector('.flex-1.overflow-y-auto') as HTMLElement;
    };

    // 统计列表，用于渲染
    const statsList = useMemo(() => [
        {
            key: 'total',
            label: t('pages.dsp.deliveryTask.totalTasks'),
            value: stats.total
        },
        {
            key: 'receiving',
            label: t('pages.dsp.deliveryTask.pickingUp'),
            value: stats.receiving
        },
        {
            key: 'delivering',
            label: t('pages.dsp.deliveryTask.delivering'),
            value: stats.delivering
        },
        {
            key: 'completed',
            label: t('pages.dsp.deliveryTask.completed'),
            value: stats.completed
        }
    ], [stats, t]);

    // 处理日期选择
    const handleDateSelect = (date: Date | null) => {
        if (date) {
            setSingleDate(date);
        }
        setShowCalendar(false);
    };

    // 处理司机选择 - 临时选择，不立即查询
    const handleDriverToggle = (driverId: string) => {
        if (driverId === 'all') {
            // 如果点击"全部司机"，清空临时选择（空数组表示全部）
            setTempSelectedDriverIds([]);
        } else {
            const driverIdNum = Number(driverId);
            setTempSelectedDriverIds(prev => {
                if (prev.includes(driverIdNum)) {
                    // 取消选择该司机
                    const newSelection = prev.filter(id => id !== driverIdNum);
                    // 如果没有选中任何司机，返回空数组（表示全部）
                    return newSelection;
                } else {
                    // 添加该司机
                    return [...prev, driverIdNum];
                }
            });
        }
    };

    // 确认司机选择 - 应用临时选择并触发查询
    const handleDriverConfirm = () => {
        setSelectedDriverIds(tempSelectedDriverIds);
        setShowDriverSelector(false);
    };

    // 取消司机选择 - 恢复原始选择
    const handleDriverCancel = () => {
        setTempSelectedDriverIds(selectedDriverIds);
        setShowDriverSelector(false);
    };

    // 获取选择器显示文本
    const getDriverSelectorText = () => {
        if (selectedDriverIds.length === 0) {
            return t('pages.dsp.deliveryTask.allDrivers');
        }
        if (selectedDriverIds.length === 1) {
            const driver = drivers.find(d => d.id === String(selectedDriverIds[0]));
            return driver?.name || t('pages.dsp.deliveryTask.allDrivers');
        }
        return `${t('pages.dsp.deliveryTask.selectedDrivers', { count: selectedDriverIds.length })}`;
    };

    // 根据任务状态获取按钮文案
    const getButtonText = (status: string) => {
        // 待司机领取或司机领取中状态显示"任务明细"
        if (status === TaskStatus.CREATED || status === TaskStatus.DRIVER_PICKINGUP) {
            return t('pages.dsp.deliveryTask.taskDetails');
        }
        // 派送中或已完成状态显示"派送情况"
        if (status === TaskStatus.DELIVERING || status === TaskStatus.COMPLETED) {
            return t('pages.dsp.deliveryTask.deliveryStatus');
        }
        // 默认显示"派送情况"
        return t('pages.dsp.deliveryTask.deliveryStatus');
    };

    // 处理刷新
    const handleRefresh = async () => {
        try {
            // 重新获取司机数据和任务数据
            await Promise.all([
                refetchDrivers(),
                refetchTasks()
            ]);
        } catch (error) {
            console.error('Refresh failed:', error);
        }
    };

    // 处理任务点击
    const handleTaskClick = (taskId: string) => {
        // 保存当前滚动位置（使用真正的滚动容器）
        const scrollContainer = getScrollContainer();
        if (scrollContainer) {
            sessionStorage.setItem('dspDeliveryTaskPageScrollTop', scrollContainer.scrollTop.toString());
        }

        // 标记来源：从任务列表进入详情页，详情页应刷新地图（不恢复上次视图）
        sessionStorage.setItem('dspTaskDetailsEntrySource', 'task-list');
        navigate(DspPath.DELIVERY_DSP_DELIVERY_TASK_DETAILS, {
            state: {
                taskId
            }
        });
    };

    // 测量并统一 label 高度
    useEffect(() => {
        // 等待 DOM 更新完成
        const timeoutId = setTimeout(() => {
            const validRefs = labelRefs.current.filter(ref => ref !== null) as HTMLDivElement[];
            if (validRefs.length === 0) return;

            // 重置所有 label 的高度，让它们自适应内容
            validRefs.forEach(ref => {
                ref.style.height = 'auto';
            });

            // 等待一帧，确保内容已渲染
            requestAnimationFrame(() => {
                // 找到所有 label 中的最大高度
                let maxHeight = 0;
                validRefs.forEach(ref => {
                    const height = ref.scrollHeight;
                    if (height > maxHeight) {
                        maxHeight = height;
                    }
                });

                // 统一设置所有 label 的高度
                if (maxHeight > 0) {
                    validRefs.forEach(ref => {
                        ref.style.height = `${maxHeight}px`;
                    });
                }
            });
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [statsList, stats]);

    // 恢复滚动位置 - 只在从派送任务相关页面返回时恢复
    useEffect(() => {
        const fromDeliveryTask = sessionStorage.getItem('fromDeliveryTask');
        if (fromDeliveryTask !== 'true' || tasksLoading || tasks.length === 0) {
            return;
        }

        const savedScrollTop = sessionStorage.getItem('dspDeliveryTaskPageScrollTop');
        if (!savedScrollTop) {
            sessionStorage.removeItem('fromDeliveryTask');
            return;
        }

        const scrollValue = parseInt(savedScrollTop, 10);
        if (isNaN(scrollValue) || scrollValue < 0) {
            sessionStorage.removeItem('dspDeliveryTaskPageScrollTop');
            sessionStorage.removeItem('fromDeliveryTask');
            return;
        }

        // 延迟恢复，确保 DOM 完全渲染
        setTimeout(() => {
            const container = getScrollContainer();
            if (container && container.scrollHeight > container.clientHeight) {
                container.scrollTop = scrollValue;
            }
            sessionStorage.removeItem('dspDeliveryTaskPageScrollTop');
            sessionStorage.removeItem('fromDeliveryTask');
        }, 300);
    }, [tasksLoading, tasks]);

    return (
        <DspLayout title={t('pages.dsp.deliveryTask.title')}>
            <PullToRefreshWrapper onRefresh={handleRefresh}>
                <div className="flex flex-col h-full bg-gray-100">
                    {/* 日期和司机选择器 - 紧凑样式 */}
                    <div className="px-3 pt-3 pb-2 bg-gray-50">
                        <div className="flex justify-between items-center">
                            {/* 日期选择器 */}
                            <Button
                                fill='none'
                                onClick={() => setShowCalendar(true)}
                                className="text-sm"
                                style={{padding: '4px 8px', minWidth: 'auto'}}
                            >
                                <div className="flex items-center">
                                    <span className="mr-1 text-gray-700">
                                        {formatLocalizedDate(singleDate, {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit'
                                        })}
                                    </span>
                                    <DownOutline className="text-gray-500" style={{fontSize: '12px'}}/>
                                </div>
                            </Button>

                            {/* 司机选择器 */}
                            <Button
                                fill='none'
                                onClick={() => {
                                    setTempSelectedDriverIds([...selectedDriverIds]);
                                    setShowDriverSelector(true);
                                }}
                                className="text-sm"
                                style={{padding: '4px 8px', minWidth: 'auto'}}
                            >
                                <div className="flex items-center">
                                    <span className="mr-1 text-gray-700">{getDriverSelectorText()}</span>
                                    <DownOutline className="text-gray-500" style={{fontSize: '12px'}}/>
                                </div>
                            </Button>
                        </div>
                    </div>

                    {/* 统计卡片 */}
                    <div className="bg-white p-3 mb-2">
                        <div className="grid grid-cols-4 gap-2 mb-3">
                            {statsList.map((item, index) => (
                                <div className="text-center flex flex-col" key={item.key}>
                                    <div
                                        ref={(el) => {
                                            labelRefs.current[index] = el;
                                        }}
                                        className="text-gray-500 text-xs mb-1 overflow-hidden text-center leading-relaxed"
                                    >
                                        {item.label}
                                    </div>
                                    <div className="text-2xl font-bold">{item.value}</div>
                                </div>
                            ))}
                        </div>

                        {/* 合计信息 */}
                        <div className="flex justify-around border-t pt-3 text-sm">
                            <div className="flex items-center gap-1">
                                <span className="text-gray-500">{t('pages.dsp.deliveryTask.total')}</span>
                                <span className="flex items-center gap-1">
                                    <PackageSvg width={16} height={16} />
                                    <span>{t('pages.dsp.deliveryTask.assigned')} {stats.totalAllocated}</span>
                                </span>
                            </div>
                            <div className="flex items-center gap-1">
                                <PackageSvg width={16} height={16} />
                                <span>{t('pages.dsp.deliveryTask.pickedUp')} {stats.totalReceived}</span>
                            </div>
                        </div>
                    </div>

                    {/* 任务列表 */}
                    <div className="flex-1 overflow-auto px-3 pb-3">
                        {tasksLoading && tasks.length === 0 ? (
                            <div className="text-center text-gray-400 py-10">
                                {t('pages.dsp.deliveryTask.loading')}
                            </div>
                        ) : tasksError ? (
                            <div className="text-center text-red-500 py-10">
                                {t('pages.dsp.deliveryTask.loadTasksFailed')}
                            </div>
                        ) : tasks.length === 0 ? (
                            <div className="text-center text-gray-400 py-10">
                                {t('pages.dsp.deliveryTask.noTasks')}
                            </div>
                        ) : (
                            tasks.map(task => {
                                const statusConfig = getStatusTag(task.status);
                                return (
                                    <div key={task.id} className="bg-white rounded-lg p-4 mb-2 shadow-sm">
                                        {/* 右：日期 */}
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="text-gray-500 text-sm text-left flex-shrink-0">{task.taskDate}</div>
                                        </div>
                                        {/* 三段码、状态 */}
                                        <div className="flex items-center justify-between mb-3">
                                            {/* 左：任务号 */}
                                            <div className="font-semibold text-base truncate flex-1 mr-3">{task.sortingCode}</div>

                                            {/* 中：状态 */}
                                            <Tag
                                                fill="solid"
                                                className="mx-2"
                                                style={{
                                                    borderRadius: '4px',
                                                    fontSize: '12px',
                                                    fontWeight: 'bold',
                                                    backgroundColor: statusConfig.backgroundColor,
                                                    color: statusConfig.textColor,
                                                    padding: '2px 8px',
                                                    border: 'none'
                                                }}
                                            >
                                                {statusConfig.text}
                                            </Tag>

                                        </div>

                                        {/* 任务号、分配和领取信息 */}
                                        <div className="flex items-center mb-3 text-sm gap-5">
                                            {/* 左：任务号 - 均匀分布 */}
                                            <div className="text-gray-600 truncate flex-1 text-center">
                                                {task.id}
                                            </div>

                                            {/* 中：分配 - 均匀分布 */}
                                            <div className="flex items-center justify-center gap-2 flex-1">
                                                <PackageSvg width={16} height={16} />
                                                <span className="text-gray-500 whitespace-nowrap">{t('pages.dsp.deliveryTask.assigned')}</span>
                                                <span className="font-medium">{task.assignedParcels || 0}</span>
                                            </div>

                                            {/* 右：领取 - 均匀分布 */}
                                            <div className="flex items-center justify-center gap-2 flex-1">
                                                <PackageSvg width={16} height={16} />
                                                <span className="text-gray-500 whitespace-nowrap">{t('pages.dsp.deliveryTask.pickedUp')}</span>
                                                <span className="font-medium">{task.actualPickedUpParcels || 0}</span>
                                            </div>
                                        </div>

                                        {/* 司机姓名和按钮 */}
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-1 text-sm text-gray-600">
                                                <DriverSvg width={14} height={14} />
                                                <span>{task.actualDriverName || t('pages.dsp.deliveryTask.unassigned')}</span>
                                            </div>
                                            <Button
                                                color='primary'
                                                fill='outline'
                                                size='small'
                                                onClick={() => handleTaskClick(task.id)}
                                            >
                                                {getButtonText(task.status)}
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </PullToRefreshWrapper>

            {/* 日期选择器弹窗 */}
            <CalendarPicker
                visible={showCalendar}
                onClose={() => setShowCalendar(false)}
                onConfirm={handleDateSelect}
                defaultValue={singleDate}
                selectionMode="single"
                min={new Date('2025-10-20')}
                max={(() => {
                    const date = new Date();
                    date.setDate(date.getDate() + 1);
                    return date;
                })()}
                title={t('pages.dsp.deliveryTask.selectDate')}
            />

            {/* 司机选择器弹窗 */}
            <Popup
                visible={showDriverSelector}
                onMaskClick={() => setShowDriverSelector(false)}
                position='bottom'
                bodyStyle={{
                    borderTopLeftRadius: '12px',
                    borderTopRightRadius: '12px',
                    height: '60vh',
                    padding: '16px'
                }}
            >
                <div className="text-lg font-semibold mb-4">{t('pages.dsp.deliveryTask.selectDriver')}</div>
                <div className="overflow-y-auto" style={{height: 'calc(60vh - 120px)', maxHeight: 'calc(60vh - 120px)'}}>
                    {driversLoading ? (
                        <div className="text-center py-8 text-gray-500">
                            {t('pages.dsp.deliveryTask.loading')}
                        </div>
                    ) : driversError ? (
                        <div className="text-center py-8 text-red-500">
                            {t('pages.dsp.deliveryTask.loadDriversFailed')}
                        </div>
                    ) : (
                        drivers.map(driver => {
                            const isChecked = driver.id === 'all'
                                ? tempSelectedDriverIds.length === 0
                                : tempSelectedDriverIds.includes(Number(driver.id));
                            return (
                                <div
                                    key={driver.id}
                                    className="flex items-center py-3 border-b border-gray-100 cursor-pointer"
                                    onClick={() => handleDriverToggle(driver.id)}
                                >
                                    <Checkbox
                                        checked={isChecked}
                                        onChange={() => handleDriverToggle(driver.id)}
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ marginRight: '12px' }}
                                    />
                                    <span className="flex-1">{driver.name}</span>
                                </div>
                            );
                        })
                    )}
                </div>
                <div className="pt-4 border-t flex-shrink-0">
                    <div className="flex gap-2">
                        <Button
                            block
                            fill='outline'
                            onClick={handleDriverCancel}
                            className="flex-1"
                        >
                            {t('pages.dsp.deliveryTask.cancel')}
                        </Button>
                        <Button
                            block
                            color='primary'
                            onClick={handleDriverConfirm}
                            className="flex-1"
                        >
                            {t('pages.dsp.deliveryTask.confirm')}
                        </Button>
                    </div>
                </div>
            </Popup>
        </DspLayout>
    );
};

export default DspDeliveryTaskPage;

